/**
 * @extends {HTMLElement}
 */
class ProductStickyCta extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #visibilityTimeout = 0;

  /** @type {number} */
  #emitRaf = 0;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {{ visible: boolean; height: number }} */
  #stickyState = { visible: false, height: 0 };

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initPriceSpider();
    this.#bindVisibilityObserver(signal);
    this.#emitStickyState(false, 0);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#visibilityTimeout) {
      clearTimeout(this.#visibilityTimeout);
      this.#visibilityTimeout = 0;
    }

    if (this.#emitRaf) {
      cancelAnimationFrame(this.#emitRaf);
      this.#emitRaf = 0;
    }

    this.#emitStickyState(false, 0);

    this.#abortController?.abort();
    this.#abortController = null;
  }

  reinit() {
    // Preserve visibility so the bar doesn't flash hidden during a variant switch.
    // disconnectedCallback() always emits visible=false; we restore the state right after.
    const wasVisible = this.#isVisible;

    this.disconnectedCallback();
    this.connectedCallback();

    if (wasVisible) {
      this.#isVisible = true;
      this.classList.add('product-sticky-cta--visible');
      this.setAttribute('aria-hidden', 'false');
      this.#scheduleEmitStickyState();
    }
  }

  #initPriceSpider() {
    const rebind = () => {
      if (window.PriceSpider?.rebind) {
        window.PriceSpider.rebind();
      }
    };

    if (window.PriceSpider?.rebind) {
      requestAnimationFrame(rebind);
      return;
    }

    const script = document.querySelector('#pricespider-script');
    if (!script) return;

    script.addEventListener('load', rebind, { once: true });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindVisibilityObserver(signal) {
    const widget = document.querySelector('.product-details .ps-widget');
    if (!widget) return;

    const footer = document.querySelector('footer, nulo-footer, .nulo-footer');

    let isWidgetScrolledPast = false;
    let isFooterVisible = false;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === widget) {
            const hasScrolledPastWidget = entry.boundingClientRect.top < 0;
            isWidgetScrolledPast = !entry.isIntersecting && hasScrolledPastWidget;
          } else if (footer && (entry.target === footer || footer.contains(entry.target))) {
            const visibleHeight = window.innerHeight - entry.boundingClientRect.top;
            const targetHeight = entry.boundingClientRect.height || footer.offsetHeight;
            const threshold50 = Math.min(targetHeight, window.innerHeight) * 0.5;

            isFooterVisible = entry.isIntersecting && (entry.intersectionRatio >= 0.5 || visibleHeight >= threshold50);
          }
        }

        const nextVisible = isWidgetScrolledPast && !isFooterVisible;

        if (this.#visibilityTimeout) {
          clearTimeout(this.#visibilityTimeout);
        }

        this.#visibilityTimeout = window.setTimeout(() => {
          if (this.#isVisible === nextVisible) return;

          this.#isVisible = nextVisible;
          this.classList.toggle('product-sticky-cta--visible', nextVisible);
          this.setAttribute('aria-hidden', nextVisible ? 'false' : 'true');
          this.#scheduleEmitStickyState();
        }, 90);
      },
      {
        threshold: [0, 0.01, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px 0px 8px 0px',
      }
    );

    this.#observer.observe(widget);
    if (footer) {
      this.#observer.observe(footer);
    }

    const onLayoutChange = () => this.#scheduleEmitStickyState();
    window.addEventListener('resize', onLayoutChange, { signal });
    window.addEventListener('orientationchange', onLayoutChange, { signal });

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(onLayoutChange);
      this.#resizeObserver.observe(this);
    }
  }

  #scheduleEmitStickyState() {
    if (this.#emitRaf) cancelAnimationFrame(this.#emitRaf);
    this.#emitRaf = requestAnimationFrame(() => {
      this.#emitRaf = 0;
      const height = this.#isVisible ? Math.ceil(this.getBoundingClientRect().height) : 0;
      this.#emitStickyState(this.#isVisible, height);
    });
  }

  /**
   * @param {boolean} visible
   * @param {number} height
   */
  #emitStickyState(visible, height) {
    const detail = { visible, height };
    const prev = this.#stickyState;

    if (prev.visible === detail.visible && prev.height === detail.height) return;

    this.#stickyState = detail;
    window.__NULO_STICKY_CTA__ = detail;
    window.dispatchEvent(new CustomEvent('nulo:sticky-cta-state', { detail }));
  }
}

if (!customElements.get('product-sticky-cta')) {
  customElements.define('product-sticky-cta', ProductStickyCta);
}
